import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowId,
  GridRowParams
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';

interface User {
  id: number;
  username: string;
  full_name: string;
  role: 'Admin' | 'Gerente';
  is_active: boolean;
  created_at: string;
}

interface UserFormData {
  username: string;
  password: string;
  full_name: string;
  role: 'Admin' | 'Gerente';
  is_active: boolean;
}

const initialFormData: UserFormData = {
  username: '',
  password: '',
  full_name: '',
  role: 'Gerente',
  is_active: true
};

export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Partial<UserFormData>>({});

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/users');
      if (!response.ok) {
        throw new Error('Error al cargar usuarios');
      }
      const data = await response.json();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setDialogMode('create');
    setSelectedUser(null);
    setFormData(initialFormData);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setDialogMode('edit');
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '', // Don't prefill password for security
      full_name: user.full_name,
      role: user.role,
      is_active: user.is_active
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleDeleteUser = async (userId: number) => {
    if (!confirm('¿Está seguro de que desea eliminar este usuario?')) {
      return;
    }

    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar usuario');
      }

      setSuccess('Usuario eliminado exitosamente');
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<UserFormData> = {};

    if (!formData.username.trim()) {
      errors.username = 'El nombre de usuario es requerido';
    }

    if (dialogMode === 'create' && !formData.password.trim()) {
      errors.password = 'La contraseña es requerida';
    }

    if (!formData.full_name.trim()) {
      errors.full_name = 'El nombre completo es requerido';
    }

    if (!formData.role) {
      errors.role = 'El rol es requerido';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const url = dialogMode === 'create' ? '/api/users' : `/api/users/${selectedUser?.id}`;
      const method = dialogMode === 'create' ? 'POST' : 'PUT';
      
      const body: any = {
        username: formData.username,
        full_name: formData.full_name,
        role: formData.role,
        is_active: formData.is_active
      };

      // Only include password if provided (required for create, optional for edit)
      if (formData.password.trim()) {
        body.password = formData.password;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar usuario');
      }

      setSuccess(
        dialogMode === 'create' 
          ? 'Usuario creado exitosamente' 
          : 'Usuario actualizado exitosamente'
      );
      setDialogOpen(false);
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFormData(initialFormData);
    setFormErrors({});
    setSelectedUser(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'username',
      headerName: 'Usuario',
      flex: 1,
      minWidth: 120
    },
    {
      field: 'full_name',
      headerName: 'Nombre Completo',
      flex: 2,
      minWidth: 200
    },
    {
      field: 'role',
      headerName: 'Rol',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Box
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: params.value === 'Admin' ? 'primary.main' : 'secondary.main',
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: 'bold'
          }}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: 'is_active',
      headerName: 'Estado',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Box
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: params.value ? 'success.main' : 'error.main',
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: 'bold'
          }}
        >
          {params.value ? 'Activo' : 'Inactivo'}
        </Box>
      )
    },
    {
      field: 'created_at',
      headerName: 'Fecha Creación',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => new Date(params.value).toLocaleDateString('es-CO')
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 120,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="Editar usuario">
              <EditIcon />
            </Tooltip>
          }
          label="Editar"
          onClick={() => handleEditUser(params.row as User)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Eliminar usuario">
              <DeleteIcon />
            </Tooltip>
          }
          label="Eliminar"
          onClick={() => handleDeleteUser(params.id as number)}
        />
      ]
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Gestión de Usuarios
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleCreateUser}
          sx={{ height: 'fit-content' }}
        >
          Crear Usuario
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <DataGrid
        rows={users}
        columns={columns}
        loading={loading}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } }
        }}
        disableRowSelectionOnClick
        sx={{
          height: 600,
          '& .MuiDataGrid-cell': {
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'grey.50',
            borderBottom: 2,
            borderColor: 'divider',
          }
        }}
      />

      {/* User Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Crear Nuevo Usuario' : 'Editar Usuario'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nombre de Usuario"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              error={!!formErrors.username}
              helperText={formErrors.username}
              fullWidth
              required
            />

            <TextField
              label={dialogMode === 'create' ? 'Contraseña' : 'Nueva Contraseña (opcional)'}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              error={!!formErrors.password}
              helperText={formErrors.password}
              fullWidth
              required={dialogMode === 'create'}
            />

            <TextField
              label="Nombre Completo"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              error={!!formErrors.full_name}
              helperText={formErrors.full_name}
              fullWidth
              required
            />

            <FormControl fullWidth required error={!!formErrors.role}>
              <InputLabel>Rol</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'Admin' | 'Gerente' })}
                label="Rol"
              >
                <MenuItem value="Admin">Administrador</MenuItem>
                <MenuItem value="Gerente">Gerente</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Usuario Activo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {dialogMode === 'create' ? 'Crear' : 'Actualizar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagement;